/**
 * ブランド新規作成モーダル (改・docs/features/brand-product-restructure-plan.md §2.5)。
 *
 * モーダルのまま 3 ステップで、呼び出し元 (案件登録・シーン追加・テンプレートの URL 選択・ブランド一覧) から離れずに
 * ブランドを完成させて戻れる。
 *
 *   1. 登録方法: 案件の新規登録の入口と同じ 2 択 (利用者指示 2026-10-30・URL ありきにしない)。
 *              「URLからブランド情報を生成する」= ブランド URL を入れて「AIでブランド情報を生成」(有料・brand_site_proposal・§2.3)。
 *              押した瞬間に 2 枚のカードをやめ、確認ステップと同じ素のモーダルの欄 (URL 欄 + 段階表示) で描く (生成中に手動へ逃げる道を残さない)
 *              「手動で入力する」= 「入力へ進む」で確認ステップへ。URL は任意の入力欄として出し、AI の生成ボタンは出さない
 *   2. 確認   : 名前 / タグライン / 説明 (提案値は入力欄に入っていて直せる)・配色と書体 (チェックで採用)・ロゴ候補 (チェックで登録)・
 *              サイト URL (公式サイトと判定できたときだけ既定で登録)
 *   3. 作成   : 確定ボタン (文言は呼び出し元が渡す) → createBrand → URL 登録 → ロゴ登録。URL / ロゴだけ失敗したら
 *              ブランドは作成済みとして警告と「再試行」を出す (部分失敗)
 *
 * - initialValues (案件 URL 読み取りの new_brand_draft 等) があるときは startAt='confirm' でステップ 2 から開く。配色・書体・ロゴ候補は
 *   そこでも「AIでブランド情報を生成」を押したときだけ取得する (3 項目だけで登録するのも正式な経路)
 * - 細かい調整 (パレットの役割・面の様式・ガイドライン画像) は扱わず、作成後に「ブランドの詳細で整える」リンクを出す
 */
import { ArrowSteps } from '../../common/ArrowSteps';
import { useConfirmDialog } from '../../common/useConfirmDialog';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Circle, CircleDot, SquareSparkles, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import LogoManagementModal from '../../../components/LogoManagementModal';
import { HelpTooltip } from '../../common/HelpTooltip';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { Z_INDEX } from '../../../styles/zIndex';
import { GenerateButton } from '../common/GenerateButton';
import { JobStepper } from '../../common/JobStepper';
import { createBrandUrl, isValidBrandUrl } from '../../../features/brands/urls';
import { createBrand } from '../../../features/brands/api';
import { cleanDesignLanguageForSave, type BrandDesignLanguage } from '../../../features/brands/designLanguage';
import {
  SITE_PROPOSAL_STAGE_LABELS,
  officialNotice,
  proposedDesignLanguage,
  registerProposedLogo,
  siteProposalStages,
  type SiteProposalLogo,
} from '../../../features/brands/siteProposal';
import { useBrandSiteProposal } from '../../../features/brands/useBrandSiteProposal';
import { updateScenario } from '../../../features/scenarios/api';
import { fillEmptyIdentity, type CreateBrandForm as CreateForm } from './createBrandForm';
import styles from './CreateBrandModal.module.css';
import '../../../styles/buttons.css';

export interface CreateBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 作成して閉じたとき。brand は呼び出し元が一覧の再取得を待たずに名前を使えるように渡す */
  onBrandCreated?: (brandId: string, brand?: { brand_name: string }) => void;
  requireLogoSetup?: boolean;
  scenarioId?: string;
  /** 開いたときにフォームへ入れる初期値 (案件 URL 読み取りの「新規ブランドとして登録」が new_brand_draft を渡す) */
  initialValues?: {
    brand_name?: string;
    /** 案件 URL 読み取りが提案したブランドコード (使用済みでないことを確かめたもの) */
    brand_code?: string;
    tagline?: string;
    description?: string;
    /** 案件 URL 読み取りから推定した公式サイトの候補 (確信度の高い順)。先頭が high / medium なら URL 欄の初期値にし、全部を選択肢として出す */
    site_url_candidates?: ReadonlyArray<SiteUrlCandidate>;
  } | null;
  /** 開く位置。'url' (既定) = ステップ 1、'confirm' = 初期値があるのでステップ 2 から */
  startAt?: 'url' | 'confirm';
  /** 確定ボタンの文言 (既定「このブランドを登録する」。案件フォームからは「登録して案件に紐づける」等) */
  confirmLabel?: string;
}

const EMPTY_FORM: CreateForm = { brand_name: '', brand_code: '', tagline: '', description: '' };
const DEFAULT_URL_NAME = '公式サイト';

type Step = 1 | 2;
/** ステップ 1 で選んだ登録方法。null = 未選択 (startAt='confirm' で開いたとき) */
type Route = 'url' | 'manual' | null;

export interface SiteUrlCandidate {
  url: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

const CONFIDENCE_LABELS: Record<SiteUrlCandidate['confidence'], string> = { high: '確信度 高', medium: '確信度 中', low: '確信度 低' };

/** URL 欄の初期値にする候補。low (読み取り元のサイトなど根拠の薄いもの) は既定で入れず、選択肢に出すだけ */
function defaultSiteUrl(candidates: ReadonlyArray<SiteUrlCandidate> | undefined): string {
  const top = candidates?.[0];
  return top && top.confidence !== 'low' ? top.url : '';
}

/** 作成後の後処理 (URL 登録・ロゴ登録) の失敗。再試行の対象 */
interface PostCreateFailures {
  url: boolean;
  logos: number[];
}

function errorText(err: unknown, fallback: string): string {
  const resp = (err as { response?: { data?: { error?: string } } })?.response;
  if (resp?.data?.error) return resp.data.error;
  return err instanceof Error && err.message ? err.message : fallback;
}

export function CreateBrandModal({
  isOpen, onClose, onBrandCreated, requireLogoSetup, scenarioId, initialValues, startAt, confirmLabel,
}: CreateBrandModalProps) {
  const queryClient = useQueryClient();
  const proposal = useBrandSiteProposal();
  // 開いている間は背面 (ページや親モーダル) をスクロールさせない (利用者指示 2026-10-31)
  useLockBodyScroll(isOpen);

  const [step, setStep] = useState<Step>(1);
  const [route, setRoute] = useState<Route>(null);
  const [url, setUrl] = useState('');
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Set<string>>(new Set());
  // 提案の採用: 配色・書体 / ロゴ候補 (チェック) / サイト URL の登録
  const [adoptDesign, setAdoptDesign] = useState(true);
  const [selectedLogos, setSelectedLogos] = useState<Set<number>>(new Set());
  const [registerUrl, setRegisterUrl] = useState(true);
  // 作成
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; brand_name: string } | null>(null);
  const [failures, setFailures] = useState<PostCreateFailures>({ url: false, logos: [] });
  const [retrying, setRetrying] = useState(false);
  const [logoBrandId, setLogoBrandId] = useState<string | null>(null);
  // 公式サイト候補のポップオーバー (URL 欄の ▾ で開く・コンボボックス形式・利用者決定 2026-10-31)。モーダルの中身は overflow で
  // 切れるので、HelpTooltip と同じく body に portal して fixed で欄の直下に置く
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [candidatesPos, setCandidatesPos] = useState({ top: 0, left: 0, width: 0 });
  const urlFieldRef = useRef<HTMLDivElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const candidatesRef = useRef<HTMLDivElement>(null);

  // 開くたびに初期値と開始位置を反映する (閉じるときは resetAll が戻す)
  useEffect(() => {
    if (!isOpen) return;
    setStep(startAt === 'confirm' ? 2 : 1);
    if (initialValues) {
      setForm((prev) => ({
        ...prev,
        brand_name: initialValues.brand_name ?? prev.brand_name,
        brand_code: initialValues.brand_code ?? prev.brand_code,
        tagline: initialValues.tagline ?? prev.tagline,
        description: initialValues.description ?? prev.description,
      }));
      const defaultUrl = defaultSiteUrl(initialValues.site_url_candidates);
      if (defaultUrl) setUrl(defaultUrl);
    }
  }, [isOpen, initialValues, startAt]);

  const siteUrlCandidates = initialValues?.site_url_candidates ?? [];

  const updateCandidatesPos = useCallback(() => {
    const field = urlFieldRef.current;
    if (!field) return;
    const rect = field.getBoundingClientRect();
    const height = candidatesRef.current?.getBoundingClientRect().height ?? 0;
    let top = rect.bottom + 4;
    if (height > 0 && top + height > window.innerHeight - 8 && rect.top - height - 4 >= 8) top = rect.top - height - 4;
    setCandidatesPos({ top, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!candidatesOpen) return;
    updateCandidatesPos();
    window.addEventListener('scroll', updateCandidatesPos, true);
    window.addEventListener('resize', updateCandidatesPos);
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCandidatesOpen(false); };
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (urlFieldRef.current?.contains(target) || candidatesRef.current?.contains(target)) return;
      setCandidatesOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('scroll', updateCandidatesPos, true);
      window.removeEventListener('resize', updateCandidatesPos);
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [candidatesOpen, updateCandidatesPos]);

  // 生成・作成が始まったら閉じる (欄が disabled になる)
  useEffect(() => {
    if (proposal.phase === 'running' || creating) setCandidatesOpen(false);
  }, [proposal.phase, creating]);

  const result = proposal.result;
  const designLanguage = useMemo<BrandDesignLanguage | null>(() => (result ? proposedDesignLanguage(result, null) : null), [result]);
  const notice = result ? officialNotice(result.identity, result.source_url) : null;
  const registrableLogos = useMemo(() => (result?.logos ?? []).filter((l) => l.registrable !== false && !!l.signed_url), [result]);
  const sourceUrl = result?.source_url ?? url.trim();

  const resetAll = () => {
    setStep(1);
    setRoute(null);
    setUrl('');
    setForm(EMPTY_FORM);
    setErrors(new Set());
    setAdoptDesign(true);
    setSelectedLogos(new Set());
    setRegisterUrl(true);
    setCreating(false);
    setCreateError(null);
    setCreated(null);
    setFailures({ url: false, logos: [] });
    setRetrying(false);
    setCandidatesOpen(false);
    proposal.reset();
  };

  const handlePropose = async () => {
    const target = url.trim();
    if (!isValidBrandUrl(target)) {
      setErrors(new Set(['url']));
      return;
    }
    setErrors(new Set());
    setRoute('url');
    const data = await proposal.start(target);
    if (!data) return;
    setForm((prev) => fillEmptyIdentity(prev, data));
    setAdoptDesign(true);
    setSelectedLogos(new Set(data.logos.filter((l) => l.registrable !== false && !!l.signed_url).map((l) => l.index)));
    // 公式サイトと明示的に判定できたときだけ読み取り元 URL を「公式サイト」として既定登録する。販売店・代理店の可能性
    // (false) はもちろん、判定できない (null・identity 無し) ときも候補として見せるだけにする (§2.3.1)
    setRegisterUrl(data.identity?.official.is_official === true);
    setStep(2);
  };

  const proceedManual = () => {
    setErrors(new Set());
    setRoute('manual');
    setStep(2);
  };

  const validate = (): boolean => {
    const next = new Set<string>();
    if (!form.brand_name.trim()) next.add('brand_name');
    const target = url.trim();
    if (target && !isValidBrandUrl(target)) next.add('url');
    setErrors(next);
    return next.size === 0;
  };

  /** URL 登録とロゴ登録 (作成直後と再試行で共用)。失敗したものを返す */
  const runPostCreate = async (brandId: string, todo: { url: boolean; logos: SiteProposalLogo[] }): Promise<PostCreateFailures> => {
    const failed: PostCreateFailures = { url: false, logos: [] };
    if (todo.url) {
      try {
        await createBrandUrl(brandId, { name: DEFAULT_URL_NAME, url: url.trim() });
        queryClient.invalidateQueries({ queryKey: ['all-brand-urls'] });
        queryClient.invalidateQueries({ queryKey: ['brand-urls', brandId] });
      } catch {
        failed.url = true;
      }
    }
    for (const logo of todo.logos) {
      try {
        await registerProposedLogo(brandId, logo);
      } catch {
        failed.logos.push(logo.index);
      }
    }
    if (todo.logos.length > failed.logos.length) queryClient.invalidateQueries({ queryKey: ['logos'] });
    return failed;
  };

  const handleCreate = async () => {
    if (!validate() || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const dl = adoptDesign && designLanguage ? cleanDesignLanguageForSave({ ...designLanguage, sourceUrl: designLanguage.sourceUrl ?? sourceUrl }) : null;
      const response = await createBrand({
        brand_name: form.brand_name.trim(),
        brand_code: form.brand_code.trim() || undefined,
        tagline: form.tagline.trim() || undefined,
        description: form.description.trim() || undefined,
        ...(dl ? { design_language: dl } : {}),
      });
      const brand = response.brand;
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-brand-logo'] });
      const todo = {
        url: registerUrl && !!url.trim() && isValidBrandUrl(url.trim()),
        logos: registrableLogos.filter((l) => selectedLogos.has(l.index)),
      };
      const failed = await runPostCreate(brand.id, todo);
      setFailures(failed);
      setCreated({ id: brand.id, brand_name: brand.brand_name || form.brand_name.trim() });
    } catch (err) {
      setCreateError(errorText(err, 'ブランドの作成に失敗しました'));
    } finally {
      setCreating(false);
    }
  };

  const handleRetry = async () => {
    if (!created || retrying) return;
    setRetrying(true);
    try {
      const failed = await runPostCreate(created.id, {
        url: failures.url,
        logos: registrableLogos.filter((l) => failures.logos.includes(l.index)),
      });
      setFailures(failed);
    } finally {
      setRetrying(false);
    }
  };

  const finish = (brandIdToNotify?: string) => {
    const notify = brandIdToNotify ? { id: brandIdToNotify, brand_name: created?.brand_name ?? '' } : created;
    resetAll();
    onClose();
    if (notify && onBrandCreated) onBrandCreated(notify.id, { brand_name: notify.brand_name });
  };

  // 確認はブラウザの confirm でなく独自モーダル (.claude/rules/browser-dialogs.md)
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirmDialog();

  const handleAttemptClose = async () => {
    if (requireLogoSetup && created) {
      const ok = await confirmAction({
        title: 'ロゴを登録せずに閉じますか？',
        message: 'ブランドは作成済みですが、ロゴがまだ登録されていません。',
        confirmLabel: '閉じる',
        variant: 'warning',
      });
      if (!ok) return;
    }
    if (!created && (proposal.phase === 'running' || creating)) return;
    finish();
  };

  const handleLogoSetup = () => {
    if (!created) return;
    const brandId = created.id;
    setLogoBrandId(brandId);
    // ブランド作成モーダルは閉じ、onBrandCreated はロゴモーダルを閉じたときに呼ぶ
    resetAll();
    onClose();
  };

  if (!isOpen && !logoBrandId) return null;

  const busy = proposal.phase === 'running' || creating;
  const hasFailures = failures.url || failures.logos.length > 0;
  const confirmText = confirmLabel ?? 'このブランドを登録する';
  const canPropose = !!url.trim() && proposal.creditsPerRun !== null && !busy;

  const renderProposeRow = (compact: boolean) => {
    const hasCandidates = !compact && siteUrlCandidates.length > 0;
    return (
      <div className={`${styles.field} ${styles.spanFull}`}>
        {!compact && (
          // htmlFor で入力欄に結び付ける。無いと label の中の HelpTooltip のボタンが「最初のラベル付け可能要素」になり、
          // ラベルのどこを押してもヘルプが開いてしまう (利用者指摘 2026-10-31)
          <label htmlFor="create-brand-url">
            ブランドURL
            <HelpTooltip ariaLabel="ブランドURLの使い方">
              <p style={{ margin: '0 0 6px 0' }}>公式サイトやブランドページのURLです。「AIでブランド情報を生成」で、AIがページを読んで名前・説明・配色・書体・ロゴの候補を作ります (有料)。</p>
              <p style={{ margin: 0 }}>提案は自動では保存されません。次の画面で確認してから登録します。URLは「公式サイト」としてブランドに登録されます。</p>
            </HelpTooltip>
          </label>
        )}
        <div className={styles.proposeRow}>
          {/* URL 欄が本体 (直接編集 = カスタム URL)。右端に × (消す) と、候補があるときは ▾ (候補を開く) */}
          <div ref={urlFieldRef} className={`${styles.urlField} ${hasCandidates ? styles.urlFieldWithCandidates : ''}`}>
            <input
              ref={urlInputRef}
              id="create-brand-url"
              type="url"
              className={`${styles.input} ${errors.has('url') ? styles.inputError : ''}`}
              value={url}
              onChange={(e) => { setUrl(e.target.value); clearUrlError(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  if (canPropose) void handlePropose();
                }
              }}
              placeholder="https://example.com"
              disabled={busy}
              aria-label="ブランドURL"
              autoFocus={!compact}
            />
            <div className={styles.urlFieldButtons}>
              {url !== '' && !busy && (
                <button
                  type="button"
                  className={styles.urlFieldButton}
                  aria-label="URLを消す"
                  title="URLを消す"
                  onClick={() => { setUrl(''); clearUrlError(); setCandidatesOpen(false); urlInputRef.current?.focus(); }}
                  data-testid="create-brand-url-clear"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
              {hasCandidates && (
                <button
                  type="button"
                  className={`${styles.urlFieldButton} ${candidatesOpen ? styles.urlFieldButtonOpen : ''}`}
                  aria-label="公式サイトの候補を開く"
                  title="読み取りから推定した公式サイトの候補"
                  aria-expanded={candidatesOpen}
                  aria-controls="create-brand-site-candidates"
                  disabled={busy}
                  onClick={() => setCandidatesOpen((v) => !v)}
                  data-testid="create-brand-site-candidates-toggle"
                >
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
          <GenerateButton
            className="btn-ai-director-md"
            label={proposal.phase === 'running' ? '生成しています…' : 'AIでブランド情報を生成'}
            creditCost={proposal.creditBadge}
            onClick={() => void handlePropose()}
            disabled={!canPropose}
            data-testid="create-brand-propose"
          />
        </div>
        {hasCandidates && candidatesOpen && createPortal(renderSiteUrlCandidates(), document.body)}
        {renderProposeStatus()}
      </div>
    );
  };

  /** 案件 URL 読み取りから推定した公式サイトの候補。URL 欄の ▾ で欄の直下にポップオーバーで出す (コンボボックス形式・利用者決定 2026-10-31)。
   *  ラジオ風の印で「いくつかから選ぶ」と示し、欄の値と一致する候補の印を埋める。選ぶと欄に入って閉じる。
   *  選定理由は画面に出さず title (ホバー) だけ。読み取り元のサイトは low で最後 */
  const renderSiteUrlCandidates = () => {
    const current = url.trim().replace(/\/+$/, '').toLowerCase();
    return (
      <div
        ref={candidatesRef}
        id="create-brand-site-candidates"
        className={styles.siteCandidates}
        style={{ position: 'fixed', top: `${candidatesPos.top}px`, left: `${candidatesPos.left}px`, width: `${candidatesPos.width}px`, zIndex: Z_INDEX.popover }}
        data-testid="create-brand-site-candidates"
      >
        <span className={styles.siteCandidatesHint}>読み取りから推定した公式サイトの候補 (選ぶとURL欄に入ります)</span>
        <ul className={styles.siteCandidateList}>
          {siteUrlCandidates.map((c) => {
            const selected = current !== '' && c.url.replace(/\/+$/, '').toLowerCase() === current;
            const Mark = selected ? CircleDot : Circle;
            return (
              <li key={c.url}>
                <button
                  type="button"
                  className={`${styles.siteCandidate} ${selected ? styles.siteCandidateSelected : ''}`}
                  onClick={() => { setUrl(c.url); clearUrlError(); setCandidatesOpen(false); urlInputRef.current?.focus(); }}
                  aria-pressed={selected}
                  title={c.reason || undefined}
                >
                  <Mark size={14} className={styles.siteCandidateMark} aria-hidden="true" />
                  <span className={styles.siteCandidateUrl}>{c.url}</span>
                  <span className={`${styles.confidence} ${styles[`confidence_${c.confidence}`]}`}>{CONFIDENCE_LABELS[c.confidence]}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const clearUrlError = () => {
    if (errors.has('url')) { const n = new Set(errors); n.delete('url'); setErrors(n); }
  };

  /** URL の形式エラー・生成中の段階表示・受付失敗のエラー (ステップ 1 のカードと確認ステップの提案行で共用) */
  const renderProposeStatus = () => (
    <>
      {errors.has('url') && <div className={styles.error}>http または https のURLを入力してください</div>}
      {proposal.phase === 'running' && (
        <JobStepper
          stages={siteProposalStages(proposal.progress)}
          labels={SITE_PROPOSAL_STAGE_LABELS}
          stepText={proposal.progress?.step?.text ?? null}
          elapsed={proposal.elapsed}
          ariaLabel="ブランド情報の生成の段階"
        />
      )}
      {proposal.phase === 'error' && proposal.error && <div className={styles.error} role="alert" data-testid="create-brand-propose-error">{proposal.error}</div>}
    </>
  );

  // 案件の新規登録の入口 (ManualProductNewPage の renderEntry) と同じ 2 枚のカード。
  // 「AIでブランド情報を生成」を押したらカードの枠をやめ、モーダルの素の欄 (確認ステップの提案行と同じ) で描く (利用者指示 2026-10-30)
  const renderStep1 = () => {
    const generating = proposal.phase === 'running';
    if (generating) {
      return <div className={styles.content}>{renderProposeRow(false)}</div>;
    }
    return (
      <div className={styles.content}>
        <section className={styles.entryCard} aria-label="URLからブランド情報を生成する">
          <h4 className={styles.entryTitle}>
            <SquareSparkles size={18} aria-hidden="true" />
            URLからブランド情報を生成する
          </h4>
          <div className={styles.entryDesc}>
            <p className={styles.entryLead}>ブランドURLを入れると、AIがウェブサイトを読んでブランド情報の候補を作ります。</p>
            {/* AI で作るメリット (利用者指示 2026-10-31)。配色・書体の使い道は brand_visual.py (シナリオの静止画・動画生成) と brand_cut_motion */}
            <ul className={styles.entryMerits}>
              <li>名前・タグライン・説明・公式サイトURLが下書きとして入り、入力の手間が減ります</li>
              <li>サイトの配色と書体を拾うので、ブランドカットや動画の生成をブランドの見た目に揃えられます</li>
              <li>サイトにあるロゴの候補も見つけます。提案は次の画面で確認してから登録します</li>
            </ul>
          </div>
          <div className={styles.entryUrlRow}>
            <input
              id="create-brand-url"
              type="url"
              className={`${styles.input} ${errors.has('url') ? styles.inputError : ''}`}
              value={url}
              onChange={(e) => { setUrl(e.target.value); clearUrlError(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  if (canPropose) void handlePropose();
                }
              }}
              placeholder="https://example.com"
              disabled={busy}
              aria-label="ブランドURL"
            />
            <GenerateButton
              className="btn-ai-director-md"
              label="AIでブランド情報を生成"
              creditCost={proposal.creditBadge}
              onClick={() => void handlePropose()}
              disabled={!canPropose}
              data-testid="create-brand-propose"
            />
          </div>
          {renderProposeStatus()}
        </section>
        <section className={styles.entryCard} aria-label="手動で入力する">
          <h4 className={styles.entryTitle}>
            {/* 案件の入口と同じ Streamline のキーボード (mask で見出しの文字色に塗る) */}
            <span className={styles.entryIconKeyboard} aria-hidden="true" />
            手動で入力する
          </h4>
          <p className={styles.entryDesc}>ブランド情報を自分で入力します。</p>
          <div className={styles.entryActions}>
            <button type="button" className="btn-ai-director-md btn-ai-director-gray" onClick={proceedManual} data-testid="create-brand-manual">
              <span className="btn-label">入力へ進む</span>
            </button>
          </div>
        </section>
      </div>
    );
  };

  /** 手動入力の経路の URL 欄 (任意)。入れたら「公式サイト」として登録する。AI の生成ボタンは出さない */
  const renderManualUrlRow = () => (
    <div className={`${styles.field} ${styles.spanFull}`}>
      <label htmlFor="create-brand-manual-url">
        ブランドURL（任意）
        <HelpTooltip ariaLabel="ブランドURLの使い方">
          <p style={{ margin: 0 }}>公式サイトやブランドページのURLです。入れると「公式サイト」としてブランドに登録されます。AIで名前や配色の候補を作りたいときは、「戻る」から「URLからブランド情報を生成する」を選んでください。</p>
        </HelpTooltip>
      </label>
      <input
        id="create-brand-manual-url"
        type="url"
        className={`${styles.input} ${errors.has('url') ? styles.inputError : ''}`}
        value={url}
        onChange={(e) => { setUrl(e.target.value); clearUrlError(); }}
        placeholder="https://example.com"
        disabled={busy}
      />
      {errors.has('url') && <div className={styles.error}>http または https のURLを入力してください</div>}
    </div>
  );

  const renderStep2 = () => (
    <>
    <div className={styles.content}>
      {result && (
        <p className={`${styles.hint} ${styles.spanFull}`} data-testid="create-brand-proposal-note">
          サイトからの提案を入れました。内容を直してから登録できます。
          {notice && <><br /><span className={styles.warn}>{notice}</span></>}
          {result.notes.map((n) => <><br /><span key={n}>{n}</span></>)}
        </p>
      )}
      <div className={styles.field}>
        <label htmlFor="create-brand-name">
          ブランド名<span className={styles.required}>*</span>
        </label>
        <input
          id="create-brand-name"
          type="text"
          className={`${styles.input} ${errors.has('brand_name') ? styles.inputError : ''}`}
          value={form.brand_name}
          onChange={(e) => { setForm({ ...form, brand_name: e.target.value }); if (errors.has('brand_name')) { const n = new Set(errors); n.delete('brand_name'); setErrors(n); } }}
          placeholder="ブランド名を入力"
          autoFocus={!result}
          disabled={busy}
        />
        {errors.has('brand_name') && <div className={styles.error}>ブランド名は必須です</div>}
      </div>

      <div className={styles.field}>
        <label htmlFor="create-brand-code">
          ブランドコード
          <HelpTooltip ariaLabel="ブランドコードの使い方">
            <p style={{ margin: '0 0 6px 0' }}>組織内でブランドを一意に識別する内部コード (例: <code>showhin</code>)。</p>
            <p style={{ margin: '0 0 6px 0' }}>商品データを CSV / JSON で一括アップロードする際、各行の <code>brand_code</code> 列にこの値を入れると自動的にこのブランドへ紐づけられます。</p>
            <p style={{ margin: 0 }}>値は大文字・小文字を区別します。UUID 形式 (ブランドの ID) を直接入れることも可能です。</p>
          </HelpTooltip>
        </label>
        <input id="create-brand-code" type="text" className={styles.input} value={form.brand_code} onChange={(e) => setForm({ ...form, brand_code: e.target.value })} placeholder="例: showhin" disabled={busy} />
      </div>

      <div className={`${styles.field} ${styles.spanFull}`}>
        <label htmlFor="create-brand-tagline">タグライン</label>
        <input id="create-brand-tagline" type="text" className={styles.input} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="ブランドのキャッチフレーズ" disabled={busy} />
      </div>

      <div className={`${styles.field} ${styles.spanFull}`}>
        <label htmlFor="create-brand-description">説明</label>
        <textarea id="create-brand-description" className={styles.textarea} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="ブランドの説明" rows={3} disabled={busy} />
      </div>

      {/* サイト URL: 提案を受けていれば登録の可否。手動入力の経路は任意の URL 欄だけ。それ以外 (案件 URL 読み取りから
          startAt='confirm' で開いた) は URL 入力 + 提案ボタン (3 項目だけで登録するのも正式な経路) */}
      {result ? (
        sourceUrl && (
          <div className={`${styles.field} ${styles.spanFull}`}>
            <label className="form-checkbox-label">
              <input type="checkbox" checked={registerUrl} onChange={(e) => setRegisterUrl(e.target.checked)} disabled={busy} />
              <span>{sourceUrl} を「{DEFAULT_URL_NAME}」として登録する</span>
            </label>
          </div>
        )
      ) : route === 'manual' ? (
        renderManualUrlRow()
      ) : (
        renderProposeRow(false)
      )}

      {/* 配色・書体 (トーン＆マナー) */}
      {designLanguage && (
        <div className={`${styles.field} ${styles.spanFull}`} data-testid="create-brand-design">
          <label className="form-checkbox-label">
            <input type="checkbox" checked={adoptDesign} onChange={(e) => setAdoptDesign(e.target.checked)} disabled={busy} />
            <span>提案された配色・書体をトーン＆マナーとして登録する</span>
          </label>
          <div className={`${styles.designPreview} ${adoptDesign ? '' : styles.designPreviewOff}`}>
            {designLanguage.palette.length > 0 && (
              <div className={styles.swatches} aria-label="提案された配色">
                {designLanguage.palette.map((p, i) => (
                  <span key={`${p.hex}-${i}`} className={styles.swatch} title={`${p.hex} (${p.role})`}>
                    <span className={styles.swatchColor} style={{ background: p.hex }} />
                    <span className={styles.swatchHex}>{p.hex}</span>
                  </span>
                ))}
              </div>
            )}
            {(designLanguage.typography.display?.family || designLanguage.typography.body?.family) && (
              <div className={styles.designLine}>
                書体:
                {designLanguage.typography.display?.family && <> 見出し {designLanguage.typography.display.family}</>}
                {designLanguage.typography.body?.family && <> / 本文 {designLanguage.typography.body.family}</>}
              </div>
            )}
            {designLanguage.mood.length > 0 && <div className={styles.designLine}>雰囲気: {designLanguage.mood.join('・')}</div>}
            {designLanguage.summary && <div className={styles.designLine}>{designLanguage.summary}</div>}
          </div>
        </div>
      )}

      {/* ロゴ候補 */}
      {result && result.logos.length > 0 && (
        <div className={`${styles.field} ${styles.spanFull}`} data-testid="create-brand-logos">
          <label>ロゴの候補 (サイトの og:image・アイコンから。チェックしたものをロゴとして登録します。低解像度のものは参考です)</label>
          <ul className={styles.logoList}>
            {result.logos.map((logo) => {
              const ok = logo.registrable !== false && !!logo.signed_url;
              return (
                <li key={logo.index} className={styles.logoItem}>
                  {logo.signed_url && <img src={logo.signed_url} alt={`ロゴ候補 ${logo.index + 1} (${logo.source})`} className={styles.logoImage} />}
                  <span className={styles.logoMeta}>{logo.source}{logo.width && logo.height ? ` ${logo.width}×${logo.height}` : ''}</span>
                  {ok ? (
                    <label className="form-checkbox-label form-checkbox-label--compact">
                      <input
                        type="checkbox"
                        checked={selectedLogos.has(logo.index)}
                        onChange={(e) => setSelectedLogos((prev) => { const n = new Set(prev); if (e.target.checked) n.add(logo.index); else n.delete(logo.index); return n; })}
                        disabled={busy}
                      />
                      <span>ロゴとして登録</span>
                    </label>
                  ) : (
                    <span className={styles.logoMeta} title="サイトから拾った低解像度の画像はクリエイティブ素材に向かないため登録できません">低解像度のため参考のみ</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {createError && <div className={`${styles.error} ${styles.spanFull}`} role="alert">{createError}</div>}
    </div>

    {/* 確定ボタンはスクロールする本文の外に出し、区切り線つきのフッタとして常に見せる (利用者指示 2026-11-04) */}
    <div className={styles.footer}>
      <div className={`modal-actions ${styles.stepActions}`}>
        {startAt !== 'confirm' && (
          <button type="button" className="btn-modal-secondary" onClick={() => setStep(1)} disabled={busy}>
            戻る
          </button>
        )}
        {/* 確定ボタンは呼び出し元にかかわらず、案件の「登録する」と同じサブカラー (利用者指示 2026-10-30) */}
        <button type="button" className="btn-modal-sub" onClick={() => void handleCreate()} disabled={busy} data-testid="create-brand-submit">
          {creating ? '登録中...' : confirmText}
        </button>
      </div>
    </div>
    </>
  );

  const renderDone = () => created && (
    <div className={styles.successContent} data-testid="create-brand-done">
      <div className={styles.successIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </div>
      <div className={styles.successMessage}>
        <span className={styles.successBrandName}>{created.brand_name}</span> を登録しました
      </div>
      {hasFailures && (
        <div className={styles.successHint} role="alert" data-testid="create-brand-failures">
          ※ ブランドは登録できましたが、
          {failures.url && 'ブランドURLの登録'}
          {failures.url && failures.logos.length > 0 && 'と'}
          {failures.logos.length > 0 && `ロゴ${failures.logos.length}件の登録`}
          に失敗しました。
          <button type="button" className="btn-link-small" onClick={() => void handleRetry()} disabled={retrying}>
            {retrying ? '再試行中...' : '再試行'}
          </button>
        </div>
      )}
      {requireLogoSetup && <div className={styles.successHint}>続けてロゴを登録してください</div>}
      <div className={styles.successActions}>
        {!requireLogoSetup && (
          <button type="button" className={styles.btnClose} onClick={() => finish()} data-testid="create-brand-close">
            閉じる
          </button>
        )}
        {/* 確定ボタンと同じサブカラー (呼び出し元にかかわらず・利用者指示 2026-11-04) */}
        <button type="button" className={`btn-modal-sub ${styles.btnLogoSetup}`} onClick={handleLogoSetup}>
          {requireLogoSetup ? 'ロゴを登録' : '続けてロゴを登録'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {isOpen && createPortal(
        // 入力内容の消失を防ぐため、オーバーレイのクリックでは閉じない (✕ ボタンのみ)
        <div className="modal-overlay modal-overlay--nested">
          <div className="modal-card modal-card--lg">
            <div className={`modal-header ${styles.header}`}>
              <h3 className={`modal-title ${styles.title}`}>
                {/* ブランド管理の見出し・メニューと同じ Streamline のアイコン (mask で見出しの文字色に塗る) */}
                <span className={styles.titleIcon} aria-hidden="true" />
                ブランド新規作成
              </h3>
              {!created && (
                <ArrowSteps
                  className={styles.steps}
                  ariaLabel="ブランド作成の手順"
                  steps={[{ value: 1, label: '登録方法' }, { value: 2, label: '確認' }]}
                  current={step}
                />
              )}
              <button className="modal-close-button" onClick={() => void handleAttemptClose()} type="button" aria-label="閉じる" disabled={!created && busy}>
                ✕
              </button>
            </div>
            {created ? renderDone() : step === 1 ? renderStep1() : renderStep2()}
          </div>
        </div>,
        document.body,
      )}
      {confirmDialog}

      {logoBrandId && (
        <LogoManagementModal
          isOpen={true}
          onClose={async () => {
            const id = logoBrandId;
            setLogoBrandId(null);
            // シナリオにブランドを紐付けてクエリを再取得
            if (scenarioId) {
              try {
                await updateScenario(scenarioId, { brand_id: id });
              } catch { /* ignore */ }
              queryClient.invalidateQueries({ queryKey: ['scenario-brand-logo', scenarioId] });
            }
            onBrandCreated?.(id);
          }}
          brandId={logoBrandId}
        />
      )}
    </>
  );
}
